import Grid from "@mui/material/Grid";
import { useEffect, useMemo, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { isNil } from "lodash";
import { useNavigate } from "react-router";

import ReferralRequestTab from "./ReferralRequestTab";
import { TabSessionStorageResponse } from "../../../models/Api/TabSessionStorage";
import { TabData } from "../../../models/TabSessionStorage";
import PatientInfo from "../../../App/components/Header/PatientInfoHeader/PatientInfo";
import { rules } from "../../../utils/validations/Validations";
import { EligibilityInformation } from "../../../models/CheckEligibility";
import { getValue, isEmptyValue } from "../../../utils";
import { RequestAuthorizationPayload } from "../../../models/RequestAuthorization";
import { RequestAuthorizationResponse } from "../../../models/Api/RequestAuthorization";
import { EligibilityInformationResponse } from "../../../models/Api/CheckEligibility";
import { ReferralRequestor } from "../../../models/ReferralRequestor";
import { WELCOME_PAGE } from "../../../constants/ReferralPaths";
import { ReferralRequestorResponse } from "../../../models/Api/ReferralRequest";
import { OuterGrid } from "../../../App/styles/styles";

export interface PropsFromState {
  TabSessionStorage: TabSessionStorageResponse;
  referralSubmitData: RequestAuthorizationResponse;
  eligibilityInformationData: EligibilityInformationResponse;
  referralRequestor: ReferralRequestorResponse;
}
export interface PropsFromDispatch {
  saveTabSessionStorage: (SessionData: TabData) => void;
  resetStates: (actionType: string[]) => void;
  requestAuthorization: (payload: RequestAuthorizationPayload) => void;
  saveEligibilityInformation: (sessionData: EligibilityInformation) => void;
  saveReferralRequestor: (sessionData: ReferralRequestor) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const ReferralRequest: React.FC<AllProps> = ({
  saveTabSessionStorage,
  TabSessionStorage,
  resetStates,
  requestAuthorization,
  referralSubmitData,
  eligibilityInformationData,
  saveEligibilityInformation,
  saveReferralRequestor,
  referralRequestor,
}: AllProps) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [formError, setFormError] = useState<any>();
  const [eligibilityData, setEligibilityData] =
    useState<EligibilityInformation | null>(null);

  const navigate = useNavigate();

  const methods = useForm<TabData>({
    defaultValues: {},
    mode: "all",
  });

  const {
    reset,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    clearErrors,
    watch,
    unregister
  } = methods;

  const saveDetails = (data: any) => {
    if (isEmptyValue(getValue(data, "emergencyContact", {}))) {
      data.emergencyContact = null;
    }
    reset({
      ...getValues(),
      requester: {
        facilityName: getValue(data, "referralSource.facilityName"),
        facilityTypeId: getValue(data, "referralSource.facilityTypeId"),
        facilityTypeName: getValue(data, "referralSource.facilityTypeName"),
        requesterExtension: getValue(
          data,
          "referralSource.referralSourceExtension"
        ),
        requesterFax: getValue(data, "referralSource.referralSourceFax"),
        requesterName: getValue(data, "referralSource.referralSourceName"),
        requesterNpi: getValue(data, "referralSource.referralSourceNpi"),
        requesterPhoneNo: getValue(
          data,
          "referralSource.referralSourcePhoneNo"
        ),
      },
    });
    data.requester = getValues("requester");
    SubmitForm(data);
  };

  useEffect(() => {
    setFormError(TabSessionStorage);
  }, [TabSessionStorage]);

  useEffect(() => {
    const requestorInfo = sessionStorage.getItem("requestorInformation");
    const referralRequestorInfo = sessionStorage.getItem("ReferralRequest");
    const checkEligibilityInfo = sessionStorage.getItem(
      "checkEligibilityResponse"
    );
    if (
      !isNil(checkEligibilityInfo) &&
      getValue(eligibilityInformationData, "response.eligibilityId") === 0
    ) {
      const eligibilityData = JSON.parse(checkEligibilityInfo);
      saveEligibilityInformation(eligibilityData);
    }
    if (!isNil(requestorInfo)) {
      const requestorData = JSON.parse(requestorInfo);
        saveReferralRequestor(requestorData);
    }
    if (
      (checkEligibilityInfo === null && referralRequestorInfo === null) ||
      requestorInfo === null
    ) {
      navigate(WELCOME_PAGE);
    }
  }, []);

  useEffect(() => {
    const sessionFileData = sessionStorage.getItem("ReferralRequest");
    let FileDataFromSessionStorage: any = null;
    FileDataFromSessionStorage = JSON.parse(sessionFileData as string);
    if (
      !isNil(FileDataFromSessionStorage) &&
      Object.keys(FileDataFromSessionStorage).length !== 0
    ) {
      saveTabSessionStorage(FileDataFromSessionStorage);
      reset({
        ...FileDataFromSessionStorage,
        referralBlobDetail: getValue(
          FileDataFromSessionStorage,
          "referralBlobDetail"
        ),
      });
      if (eligibilityInformationData) {
        const patientEligibilityData: EligibilityInformation = {
          lastName: getValue(eligibilityInformationData, "response.lastName"),
          firstName: getValue(eligibilityInformationData, "response.firstName"),
          dob: getValue(eligibilityInformationData, "response.dob"),
          policyId: getValue(eligibilityInformationData, "response.policyId"),
          payerName: getValue(eligibilityInformationData, "response.payerName"),
          payerId: getValue(eligibilityInformationData, "response.payerId"),
          eligibilityId: getValue(
            eligibilityInformationData,
            "response.eligibilityId"
          ),
          eligibilityStatus: getValue(
            eligibilityInformationData,
            "response.eligibilityStatus"
          ),
          isBcbsmiAuth: getValue(eligibilityInformationData, "response.isBcbsmiAuth"),
          response: getValue(eligibilityInformationData, "response.response"),
        };
        setEligibilityData(patientEligibilityData);
      }
    }
    if (
      !isNil(eligibilityInformationData) &&
      getValue(eligibilityInformationData, "response.eligibilityId") > 0 &&
      isNil(FileDataFromSessionStorage)
    ) {
      reset({
        patientDetail: {
          lastName: getValue(eligibilityInformationData, "response.lastName"),
          firstName: getValue(eligibilityInformationData, "response.firstName"),
          dateOfBirth: getValue(eligibilityInformationData, "response.dob"),
          subscriberId: getValue(
            eligibilityInformationData,
            "response.policyId"
          ),
          eligibilityPayerName: getValue(
            eligibilityInformationData,
            "response.payerName"
          ),
          eligibilityPayerId: getValue(
            eligibilityInformationData,
            "response.payerId"
          ),
          isBcbsmiAuth: getValue(eligibilityInformationData, "response.isBcbsmiAuth"),
          response: getValue(eligibilityInformationData, "response.response"),
        },
      });
      setEligibilityData(getValue(eligibilityInformationData, "response"));
    }
  }, [eligibilityInformationData]);

  const SubmitForm = (data: any) => {
    if (Object.keys(data).length !== 0) {
      saveTabSessionStorage(data);
    }
  };

  const {
    fields: icd10RecordsFields,
    append: icd10RecordsAppend,
    remove: icd10RecordsRemove,
  } = useFieldArray({
    control,
    name: "diagnosisCode",
    rules: useMemo(() => {
      return currentTab === 2 ? rules.icdCodes : { required: false };
    }, [currentTab]),
  });

  const values = getValues();

  return (
    <>
      <Grid sx={OuterGrid}>
        <PatientInfo watch={watch} eligibilityData={eligibilityData} />
        <ReferralRequestTab
          setCurrentTab={setCurrentTab}
          saveTabSessionStorage={saveTabSessionStorage}
          TabSessionStorage={TabSessionStorage}
          reset={reset}
          getValues={getValues}
          control={control}
          handleSubmit={handleSubmit(saveDetails)}
          formState={formState}
          currentTab={currentTab}
          values={values}
          setValue={setValue}
          icd10RecordsFields={icd10RecordsFields}
          icd10RecordsAppend={icd10RecordsAppend}
          icd10RecordsRemove={icd10RecordsRemove}
          clearErrors={clearErrors}
          watch={watch}
          resetStates={resetStates}
          formError={formError}
          eligibilityData={eligibilityData}
          requestAuthorization={requestAuthorization}
          referralSubmitData={referralSubmitData}
          referralRequestor={referralRequestor}
          unregister={unregister}
        />
      </Grid>
    </>
  );
};

export default ReferralRequest;
